/**
 * FaturaCpfDialog
 *
 * Dialog enxuto que o consultor abre quando clica no botão "Fatura CPF"
 * (inbox lateral ou aba Faturas do lead). Lead CPF: o CPF do cliente já vem
 * preenchido. Lead CNPJ: pede o CPF do sócio e mostra o CNPJ read-only. Ao
 * confirmar chama a edge fatura-cpf-disparar, que manda o texto do segmento.
 *
 * 🛡️ Anti-retentativa (30/05/2026): assim que o código de vínculo é
 * formado, o dialog consulta se JÁ existe solicitação pra ele e mostra
 * o status de forma clara — pra o consultor não achar que não enviou e
 * ficar reenviando o mesmo CPF várias vezes (enchia o WhatsApp da Gerente).
 * Se já está ENVIADA/RECEBIDA, o botão Disparar trava.
 */
import { useState, useEffect } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { IdCard, Loader2, Send, Clock, CheckCircle2, AlertTriangle } from "lucide-react";
import { format } from "date-fns";
import { ptBR } from "date-fns/locale";
import { toast } from "sonner";

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  lead: {
    id: string;
    cnpj?: string | null;
    razao_social: string;
    lead_numero?: number | null;
    doc_cpf?: string | null;
    cpf?: string | null;
    tipo_pessoa?: string | null;
  };
  consultorId: string;
  conversationIdOrigem?: string | null;
  onSuccess?: (codigoVinculo: string) => void;
}

type StatusExistente = "ENVIADA" | "RECEBIDA" | "NAO_EXISTE" | "FALHA";

interface SolExistente {
  status: StatusExistente;
  created_at: string;
  recebida_em: string | null;
  consultor_nome: string | null;
}

function formatCpf(s: string): string {
  const d = (s || "").replace(/\D/g, "").slice(0, 11);
  if (d.length <= 3) return d;
  if (d.length <= 6) return `${d.slice(0, 3)}.${d.slice(3)}`;
  if (d.length <= 9) return `${d.slice(0, 3)}.${d.slice(3, 6)}.${d.slice(6)}`;
  return `${d.slice(0, 3)}.${d.slice(3, 6)}.${d.slice(6, 9)}-${d.slice(9)}`;
}

function fmtData(dt: string | null): string {
  if (!dt) return "";
  try { return format(new Date(dt), "dd/MM 'às' HH:mm", { locale: ptBR }); } catch { return ""; }
}

export function FaturaCpfDialog({
  open,
  onOpenChange,
  lead,
  consultorId,
  conversationIdOrigem,
  onSuccess,
}: Props) {
  const pf = lead.tipo_pessoa === "PF";
  const cpfDoLead = (pf ? lead.cpf : lead.doc_cpf) || "";
  const [cpf, setCpf] = useState(formatCpf(cpfDoLead));
  const [enviando, setEnviando] = useState(false);
  const queryClient = useQueryClient();

  // Reseta o CPF toda vez que reabre pra um lead diferente.
  useEffect(() => {
    if (open) setCpf(formatCpf(cpfDoLead));
  }, [open, lead.id, cpfDoLead]);

  const cpfDigits = cpf.replace(/\D/g, "");
  const codigoPreview =
    cpfDigits.length === 11 && lead.lead_numero
      ? `${cpfDigits}-${lead.lead_numero}`
      : "";

  // 🔎 Já existe solicitação pra esse código? (acompanhamento em tempo real)
  const { data: existente, isFetching: checando, refetch: refetchExistente } = useQuery({
    queryKey: ["fatura-cpf-existente", codigoPreview],
    enabled: open && !!codigoPreview,
    staleTime: 10_000,
    queryFn: async (): Promise<SolExistente | null> => {
      const { data, error } = await supabase
        .from("v_solicitacoes_fatura_cpf" as any)
        .select("status, created_at, recebida_em, consultor_nome")
        .eq("codigo_vinculo", codigoPreview)
        .order("created_at", { ascending: false })
        .limit(1)
        .maybeSingle();
      if (error) throw error;
      return (data as unknown as SolExistente) ?? null;
    },
  });

  // Bloqueia o disparo quando já está aguardando (ENVIADA) ou já veio (RECEBIDA).
  const travado = existente?.status === "ENVIADA" || existente?.status === "RECEBIDA";

  const handleDisparar = async () => {
    if (cpfDigits.length !== 11) {
      toast.error("CPF deve ter 11 dígitos");
      return;
    }
    if (!lead.lead_numero) {
      toast.error("Lead sem número (lead_numero) — impossível gerar código de vínculo");
      return;
    }
    if (travado) {
      toast.warning(
        existente?.status === "RECEBIDA"
          ? "Essa fatura CPF já foi recebida — baixe na aba Faturas."
          : "Essa fatura CPF já está aguardando a Gerente. Não precisa reenviar."
      );
      return;
    }
    setEnviando(true);
    try {
      const { data, error } = await supabase.functions.invoke("fatura-cpf-disparar", {
        body: {
          lead_id: lead.id,
          cpf: cpfDigits,
          consultor_id: consultorId,
          conversation_id_origem: conversationIdOrigem || null,
        },
      });
      if (error || !data?.ok) {
        const msg =
          data?.error ||
          error?.context?.body?.error ||
          error?.message ||
          "Erro desconhecido";
        toast.error(`Falha: ${msg}`, { duration: 8000 });
        return;
      }

      // 🛡️ Backend recusou por já existir — avisa claramente (não é "enviei de novo").
      if (data.duplicado) {
        toast.warning(
          data.mensagem || "Já existe solicitação pra esse CPF — não reenviado.",
          { duration: 7000 }
        );
        await refetchExistente();
        return; // mantém o dialog aberto mostrando o status
      }

      toast.success(
        `Fatura CPF solicitada! Código: ${data.codigo_vinculo}`,
        { duration: 6000 }
      );
      // Atualiza acompanhamento (aba CPF da página Faturas) na hora.
      queryClient.invalidateQueries({ queryKey: ["solicitacoes-fatura-cpf"] });
      onSuccess?.(data.codigo_vinculo);
      onOpenChange(false);
    } catch (e: any) {
      toast.error(e?.message || "Erro inesperado");
    } finally {
      setEnviando(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <IdCard className="h-5 w-5 text-primary" /> Fatura CPF
          </DialogTitle>
          <DialogDescription className="text-xs">
            {pf
              ? "Pede à Gerente Claro as contas deste cliente: a móvel em PDF e a fixa em fotos, com o mesmo código."
              : "Pede à Gerente Claro as contas do CPF do sócio, vinculadas ao CNPJ deste lead."}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2">
          {pf ? (
            <div>
              <Label className="text-xs text-muted-foreground">Cliente</Label>
              <div className="text-sm font-medium mt-1 truncate">{lead.razao_social}</div>
            </div>
          ) : (
            <div>
              <Label className="text-xs text-muted-foreground">CNPJ do lead</Label>
              <div className="flex items-center gap-2 mt-1">
                <Input value={lead.cnpj || ""} readOnly disabled />
              </div>
              <div className="text-[11px] text-muted-foreground mt-1 truncate">
                {lead.razao_social}
              </div>
            </div>
          )}

          <div>
            <Label htmlFor="cpf">{pf ? "CPF do cliente" : "CPF do sócio"}</Label>
            <Input
              id="cpf"
              value={cpf}
              onChange={(e) => setCpf(formatCpf(e.target.value))}
              placeholder="000.000.000-00"
              autoFocus
              maxLength={14}
            />
          </div>

          {codigoPreview && (
            <div className="rounded-md border bg-muted/30 p-2.5 text-[11px] leading-relaxed">
              <div className="text-muted-foreground">Código de vínculo gerado:</div>
              <div className="font-mono font-semibold text-foreground">
                {codigoPreview}
              </div>
              <div className="text-muted-foreground mt-1">
                A Gerente usa esse código no nome do PDF da móvel e na legenda das fotos da fixa.
              </div>
            </div>
          )}

          {/* 🛡️ Status de solicitação já existente — evita reenvio às cegas */}
          {codigoPreview && existente && (
            <StatusExistenteBanner sol={existente} checando={checando} />
          )}
          {codigoPreview && !existente && checando && (
            <div className="text-[11px] text-muted-foreground flex items-center gap-1.5">
              <Loader2 className="h-3 w-3 animate-spin" /> Verificando se já foi solicitada…
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)} disabled={enviando}>
            {travado ? "Fechar" : "Cancelar"}
          </Button>
          <Button onClick={handleDisparar} disabled={enviando || cpfDigits.length !== 11 || travado}>
            {enviando ? (
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
            ) : (
              <Send className="h-4 w-4 mr-2" />
            )}
            {existente?.status === "ENVIADA"
              ? "Já aguardando"
              : existente?.status === "RECEBIDA"
                ? "Já recebida"
                : "Disparar"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/** Banner que mostra o estado da última solicitação desse código. */
function StatusExistenteBanner({ sol, checando }: { sol: SolExistente; checando: boolean }) {
  const porQuem = sol.consultor_nome ? ` por ${sol.consultor_nome}` : "";

  if (sol.status === "ENVIADA") {
    return (
      <div className="rounded-md border border-primary/40 bg-primary/5 p-2.5 text-[11px] leading-relaxed">
        <div className="flex items-center gap-1.5 font-semibold text-primary">
          <Clock className="h-3.5 w-3.5" /> Já solicitada — aguardando a Gerente
        </div>
        <div className="text-muted-foreground mt-1">
          Enviada em {fmtData(sol.created_at)}{porQuem}. Não precisa reenviar —
          assim que a Gerente responder, a fatura aparece na aba Faturas.
        </div>
      </div>
    );
  }

  if (sol.status === "RECEBIDA") {
    return (
      <div className="rounded-md border border-success bg-success/10 p-2.5 text-[11px] leading-relaxed">
        <div className="flex items-center gap-1.5 font-semibold text-success">
          <CheckCircle2 className="h-3.5 w-3.5" /> Fatura já recebida
        </div>
        <div className="text-muted-foreground mt-1">
          Chegou em {fmtData(sol.recebida_em || sol.created_at)}. A conta está
          na aba Fatura do lead.
        </div>
      </div>
    );
  }

  // NAO_EXISTE ou FALHA — pode tentar de novo, mas avisa o histórico.
  const ehFalha = sol.status === "FALHA";
  return (
    <div className="rounded-md border bg-muted/40 p-2.5 text-[11px] leading-relaxed">
      <div className="flex items-center gap-1.5 font-semibold text-muted-foreground">
        <AlertTriangle className="h-3.5 w-3.5" />
        {ehFalha ? "Última tentativa falhou" : "Gerente respondeu: sem fatura"}
      </div>
      <div className="text-muted-foreground mt-1">
        {fmtData(sol.created_at)}{checando ? " · atualizando…" : ""}. Você pode
        solicitar de novo.
      </div>
    </div>
  );
}
